import { useRef } from "react";
import styles from "./styles.module.scss";
import { ChevronDown, ChevronUp, Users, Paperclip } from "lucide-react";
import { Button } from "ui-kit-v2/src/button/button";
import { _ } from "i18n";
import { clsx } from "clsx";
import { CoachReviewsAIGenerateButton } from "../../AIGenerateButton";
import { useCoachReviewsStore, getDraftKey } from "common/src/gamePage/stores/coach-reviews.store";
import { AICoachReviewsMode, AttachModeKind, TeamFilterKey } from "common/src/gamePage/types";
import { CoachReviewsSendButton } from "../../SendButton";
import { useAttachStore } from "common/src/gamePage/attach-store";
import { useGetReviews } from "common/src/gamePage/hooks/useGetReviews";
import { useActiveCoachReviewsContext } from "common/src/gamePage/hooks/useActiveCoachReviewsContext";
import { useStore as useGameStore } from "common/src/gamePage/store";
import { CoachReviewsStatusTag } from "../../StatusTag";
import { CoachReviewsAttachedEvents } from "../../AttachedEvents";
import { CoachReviewsAIGeneratedIcon } from "../../AIGeneratedIcon";
import { useAttachedEvents } from "common/src/gamePage/hooks/useAttachedEvents";

export const CoachReviewsTeam = () => {
	const { gameId, gameTeamId, userId } = useActiveCoachReviewsContext();
	const draftKey = getDraftKey({ kind: "team", userId: userId, teamId: gameTeamId, gameId: gameId });
	const draft = useCoachReviewsStore((state) => state.drafts[draftKey]);
	const upsertDraft = useCoachReviewsStore((state) => state.upsertDraft);
	const isOpen = useCoachReviewsStore((state) => state.toggleState.teamKey === draftKey);
	const toggleOpen = useCoachReviewsStore((state) => state.toggleOpen);
	const { enterAttachMode, setAttachedEvents } = useAttachStore();
	const gameData = useGameStore((state) => state.gameData);
	const { data, isLoading, isError } = useGetReviews();
	const containerRef = useRef<HTMLDivElement | null>(null);
	const wasReviewSent = Boolean(data?.data.team.review?.text);
	const attachedEvents = useAttachedEvents(wasReviewSent ? data?.data.team.review?.events : draft?.attachedEventIds);

	if (isLoading || isError || gameData === null || gameData === undefined) return null;

	const { players, team, aiGenerationAvailable } = data.data;
	const teamFilterKey = gameData.homeGameTeam.id === gameTeamId ? TeamFilterKey.Home : TeamFilterKey.Away;
	const textAreaValue = wasReviewSent ? team.review?.text : draft?.reviewText || "";
	const hasReviewText = Boolean(draft?.reviewText);
	const hasAttachedEvents = Boolean(draft?.attachedEventIds?.length || data?.data.team.review?.events?.length);
	const shouldShowBadge = hasReviewText || hasAttachedEvents || wasReviewSent;
	const eligiblePlayers = players.filter((player) => player.reviewEligibility.isEligible);

	const handleToggle = () => {
		toggleOpen({ kind: "team", userId: userId, gameId: gameId, teamId: gameTeamId });
	};

	const handleReviewTextChange = (reviewText: string) => {
		// Clearing AI-generated text resets the AI flag and drops the linked AI content id
		const shouldResetAIFlag = draft?.generatedWithAI && reviewText === "";

		upsertDraft({
			kind: "team",
			userId: userId,
			gameId: gameId,
			teamId: gameTeamId,
			reviewText: reviewText,
			...(shouldResetAIFlag && { generatedWithAI: false, contentId: null }),
		});
	};

	const handleAttachEvents = () => {
		// Prefil saved attached events for the team if any exist in the draft
		setAttachedEvents(
			{
				kind: AttachModeKind.Team,
				teamId: gameTeamId,
				name: team.teamName || "",
				filterKey: teamFilterKey,
			},
			attachedEvents
		);

		// Enter attach mode for the team
		enterAttachMode({
			kind: AttachModeKind.Team,
			teamId: gameTeamId,
			name: team.teamName || "",
			filterKey: teamFilterKey,
		});
	};

	return (
		<div className={clsx(styles.team, { [styles["team--opened"]]: isOpen })}>
			<button type="button" className={styles["team__header"]} onClick={handleToggle}>
				<div className={styles["team__header-left"]}>
					<Users size={20} />
					<p>{_("game.coachReviews.team.title", { teamName: team.teamName || "" })}</p>
					{shouldShowBadge && <CoachReviewsStatusTag status={wasReviewSent ? "sent" : "draft"} />}
					{draft?.generatedWithAI && <CoachReviewsAIGeneratedIcon />}
				</div>
				{isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
			</button>
			{isOpen && (
				<div className={styles["team__body"]}>
					<textarea
						className={styles["team__textarea"]}
						placeholder={_("game.coachReviews.team.textarea.placeholder")}
						value={textAreaValue}
						onChange={(e) => handleReviewTextChange(e.target.value)}
						disabled={wasReviewSent}
					/>
					{!wasReviewSent && (
						<div className={styles["team__actions"]} ref={containerRef}>
							{aiGenerationAvailable && (
								<CoachReviewsAIGenerateButton mode={AICoachReviewsMode.Team} compact popoverWidthRef={containerRef} />
							)}
							{!hasAttachedEvents && (
								<Button size="small" variant="neutral" iconLeft={<Paperclip size={16} />} onClick={handleAttachEvents}>
									{_("game.coachReviews.attachButton.label")}
								</Button>
							)}
						</div>
					)}
					{hasAttachedEvents && (
						<CoachReviewsAttachedEvents events={attachedEvents} onEditClick={handleAttachEvents} canEdit={!wasReviewSent} />
					)}
					<CoachReviewsSendButton
						mode={AICoachReviewsMode.Team}
						numberOfPlayers={eligiblePlayers.length}
						hasParents={team.hasParents}
						wasSent={wasReviewSent}
						disabled={!hasReviewText}
					/>
				</div>
			)}
		</div>
	);
};
